import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, CornerDownLeft, Search } from 'lucide-react';
import { useMemo, useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';
import { fetchGames } from '../lib/api';

/**
 * Game picker for PT1 — a hybrid of "type a game_uuid" and "pick by name".
 *
 * The games available to the chosen project come from the backend
 * (`/games?play_service_id=`), so support can select by the human name instead
 * of pasting a uuid. But the field is still free-text: paste any game_uuid and
 * press Enter to use it directly (a game not in the project's list, an old one,
 * whatever). Nothing about the project's game list constrains what you can look up.
 */
export function GameSelect({
  value,
  onChange,
  api,
  token,
  playServiceId,
}: {
  value: string;
  onChange: (gameUuid: string) => void;
  api: string;
  token: string | null;
  playServiceId: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const games = useQuery({
    queryKey: ['games', api, playServiceId],
    queryFn: ({ signal }) => fetchGames({ api, token, playServiceId, signal }),
    enabled: Boolean(api && playServiceId),
    staleTime: 5 * 60_000,
  });

  const list = games.data ?? [];
  const selected = list.find((g) => g.game_uuid === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (g) => g.name.toLowerCase().includes(q) || g.game_uuid.toLowerCase().includes(q),
    );
  }, [list, query]);

  const commit = (uuid: string) => {
    onChange(uuid.trim());
    setOpen(false);
    setQuery('');
  };

  const label = selected?.name ?? (value || 'Select or type game…');

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Enter commits the exact typed value as a game_uuid, unless it matches
      // exactly one game name — then use that game.
      const q = query.trim();
      if (!q) return;
      const exact = filtered.length === 1 ? filtered[0] : undefined;
      commit(exact ? exact.game_uuid : q);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-[280px] items-center gap-2 border border-hairline bg-surface-sunken px-2 py-1.5 text-left',
            'hover:border-hairline-strong',
            open && 'border-accent',
          )}
        >
          <span className="font-condensed text-[9px] uppercase tracking-wider text-text-faint">
            game
          </span>
          <span
            className={cn(
              'flex-1 truncate font-mono text-[12px]',
              value ? 'text-text' : 'text-text-faint',
            )}
          >
            {label}
          </span>
          {selected?.type && (
            <span className="shrink-0 font-condensed text-[8px] uppercase tracking-wider text-text-faint">
              {selected.type}
            </span>
          )}
          <ChevronsUpDown size={12} className="shrink-0 text-text-faint" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[340px] border border-hairline-strong bg-surface-raised shadow-xl shadow-black/50"
        >
          <div className="flex items-center gap-2 border-b border-hairline px-2">
            <Search size={12} className="shrink-0 text-text-faint" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search by name, or paste a game_uuid…"
              spellCheck={false}
              className="w-full bg-transparent py-2 font-mono text-[12px] text-text placeholder:text-text-faint focus:outline-none"
            />
          </div>

          {/* Manual-entry escape hatch: use whatever was typed as the game_uuid. */}
          {query.trim() && !filtered.some((g) => g.game_uuid === query.trim()) && (
            <button
              type="button"
              onClick={() => commit(query)}
              className="flex w-full items-center gap-2 border-b border-hairline bg-surface-sunken/60 px-2 py-1.5 text-left hover:bg-surface-sunken"
            >
              <CornerDownLeft size={12} className="shrink-0 text-accent" />
              <span className="truncate font-mono text-[12px] text-text">Use “{query.trim()}”</span>
              <span className="ml-auto shrink-0 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
                as game_uuid
              </span>
            </button>
          )}

          <div className="max-h-[320px] overflow-auto py-1">
            {!playServiceId && <Message>Select a project first to list its games.</Message>}
            {playServiceId && games.isLoading && <Message>Loading games…</Message>}
            {playServiceId && games.isError && (
              <Message tone="crit">Could not load games for this project.</Message>
            )}
            {playServiceId && !games.isLoading && !games.isError && filtered.length === 0 && (
              <Message>No game matches “{query}”. Press Enter to use it as a game_uuid.</Message>
            )}

            {filtered.map((g) => {
              const active = g.game_uuid === value;
              return (
                <button
                  key={g.game_uuid}
                  type="button"
                  onClick={() => commit(g.game_uuid)}
                  className={cn(
                    'flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-surface-sunken',
                    active && 'bg-surface-sunken',
                  )}
                >
                  <Check size={12} className={cn('shrink-0', active ? 'text-accent' : 'opacity-0')} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="truncate font-sans text-[12px] text-text">{g.name}</span>
                      {g.type && (
                        <span className="shrink-0 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
                          {g.type}
                        </span>
                      )}
                    </div>
                    <div className="truncate font-mono text-[10px] text-text-faint">{g.game_uuid}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Message({ children, tone }: { children: React.ReactNode; tone?: 'crit' }) {
  return (
    <div className={cn('px-3 py-3 text-[12px]', tone === 'crit' ? 'text-crit' : 'text-text-dim')}>
      {children}
    </div>
  );
}
